import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ReportScheduleConfigDto } from "@/types/admin-analytics";
import { validateEmail } from "@/utils/validation";
import { formatTimestamp } from "../utils/format";

/** Backend caps the recipient list at 50 (spec §9). */
const MAX_RECIPIENTS = 50;

interface ReportScheduleRowProps {
  config: ReportScheduleConfigDto;
  onSave: (enabled: boolean, recipients: string[]) => void;
  isSaving: boolean;
  canEdit: boolean;
}

export const ReportScheduleRow: React.FC<ReportScheduleRowProps> = ({
  config,
  onSave,
  isSaving,
  canEdit,
}) => {
  const { t } = useTranslation();
  const [enabled, setEnabled] = useState(config.enabled);
  const [recipients, setRecipients] = useState<string[]>(config.recipients);
  const [draft, setDraft] = useState("");
  const [draftError, setDraftError] = useState<string | null>(null);

  // Re-seed local state when the saved config changes (e.g. after a save echo).
  useEffect(() => {
    setEnabled(config.enabled);
    setRecipients(config.recipients);
  }, [config]);

  const isDirty =
    enabled !== config.enabled ||
    JSON.stringify(recipients) !== JSON.stringify(config.recipients);

  const addRecipient = () => {
    if (!canEdit) return;
    const email = draft.trim();
    if (!email) {
      setDraftError(null);
      return;
    }
    if (!validateEmail(email).isValid) {
      setDraftError(t("analytics.platform.reports.invalidEmail"));
      return;
    }
    if (recipients.includes(email)) {
      setDraftError(t("analytics.platform.reports.duplicateEmail"));
      return;
    }
    if (recipients.length >= MAX_RECIPIENTS) {
      setDraftError(
        t("analytics.platform.reports.maxRecipients", {
          max: MAX_RECIPIENTS,
        }),
      );
      return;
    }
    setRecipients((previous) => [...previous, email]);
    setDraft("");
    setDraftError(null);
  };

  const removeRecipient = (email: string) => {
    if (!canEdit) return;
    setRecipients((previous) => previous.filter((item) => item !== email));
  };

  const handleDraftKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();
      addRecipient();
    }
  };

  const periodLabel =
    config.period === "week"
      ? t("analytics.platform.reports.weekly")
      : t("analytics.platform.reports.monthly");
  const sendTimeCaption =
    config.period === "week"
      ? t("analytics.platform.reports.weeklySendTime")
      : t("analytics.platform.reports.monthlySendTime");
  const isPaused = !enabled && recipients.length > 0;
  const lastSentLabel = config.lastSentAt
    ? formatTimestamp(config.lastSentAt)
    : null;

  return (
    <div className="border-base-border bg-background flex flex-col gap-3 rounded-lg border p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Switch
            id={`report-schedule-${config.period}`}
            checked={enabled}
            onCheckedChange={setEnabled}
            disabled={!canEdit}
          />
          <div className="flex flex-col">
            <Label
              htmlFor={`report-schedule-${config.period}`}
              className="text-sm font-medium"
            >
              {periodLabel}
            </Label>
            <span className="text-muted-foreground text-xs">
              {sendTimeCaption}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-muted-foreground text-xs">
            {lastSentLabel
              ? t("analytics.platform.reports.lastSent", {
                  time: lastSentLabel,
                })
              : t("analytics.platform.reports.neverSent")}
          </span>
          <Button
            variant="new_default"
            size="sm"
            onClick={() => onSave(enabled, recipients)}
            disabled={!canEdit || isSaving || !isDirty}
          >
            {t("common.save")}
          </Button>
        </div>
      </div>

      {recipients.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {recipients.map((email) => (
            <Badge key={email} variant="new_secondary">
              {email}
              <button
                type="button"
                className="inline-flex"
                onClick={() => removeRecipient(email)}
                disabled={!canEdit}
                aria-label={t("analytics.platform.reports.removeRecipient", {
                  email,
                })}
              >
                <X className="size-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      <div className="flex flex-col gap-1">
        <Input
          value={draft}
          onChange={(event) => {
            setDraft(event.target.value);
            setDraftError(null);
          }}
          onKeyDown={handleDraftKeyDown}
          onBlur={addRecipient}
          placeholder={t("analytics.platform.reports.recipientPlaceholder")}
          className="max-w-md"
          aria-label={t("analytics.platform.reports.recipientsLabel")}
          disabled={!canEdit}
        />
        {draftError && <p className="text-destructive text-xs">{draftError}</p>}
        {isPaused && (
          <p className="text-muted-foreground text-xs">
            {t("analytics.platform.reports.pausedHint")}
          </p>
        )}
      </div>
    </div>
  );
};
